import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {memo, useRef} from 'react';
import Measure from 'react-measure';
import {shallowEqual} from 'react-redux';

import {PANEL_BANK_PADDING} from '../../util/panelbankTypes';
import {useStatsigGateWorkspaceDragAndDrop} from '../../util/rampFeatureFlags';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import PanelBankSections from './PanelBankSections';
import {PanelBankSectionsContainer} from './PanelBankSectionsContainer';
import {usePanelBankWidthContext} from './PanelBankWidthContext';
import {PanelBankConfigWithRefs, PanelBankProps} from './types';

interface PanelBankInnerProps {
  panelBankProps: PanelBankProps;
  panelBankConfigWithRefs: PanelBankConfigWithRefs;
  setPanelSelection(args: any): any;
}

const PanelBankInnerComp = ({
  panelBankProps,
  panelBankConfigWithRefs,
}: PanelBankInnerProps) => {
  const panelBankSectionsDomRef = useRef<HTMLDivElement>(null);

  const {panelBankWidth, setPanelBankWidth} = usePanelBankWidthContext();

  return (
    <Measure
      bounds
      innerRef={panelBankSectionsDomRef}
      onResize={contentRect => {
        const newWidth = contentRect.bounds
          ? Math.floor(contentRect.bounds.width) - PANEL_BANK_PADDING * 2
          : 0;
        if (newWidth !== panelBankWidth) {
          setPanelBankWidth(newWidth);
        }
      }}>
      {({measureRef}) => {
        return (
          <PanelBankSectionsRouter
            measureRef={measureRef}
            panelBankConfigWithRefs={panelBankConfigWithRefs}
            panelBankProps={panelBankProps}
            panelBankSectionsDomRef={panelBankSectionsDomRef}
            panelBankWidth={panelBankWidth}
          />
        );
      }}
    </Measure>
  );
};

const PanelBankSectionsRouter = ({
  measureRef,
  panelBankConfigWithRefs,
  panelBankProps,
  panelBankSectionsDomRef,
  panelBankWidth,
}: {
  measureRef: (ref: Element | null) => void;
  panelBankConfigWithRefs: PanelBankConfigWithRefs;
  panelBankProps: PanelBankProps;
  panelBankSectionsDomRef: React.RefObject<HTMLDivElement>;
  panelBankWidth: number;
}) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();

  const isNewWsDragAndDropEnabled = useStatsigGateWorkspaceDragAndDrop();

  if (isNewWsDragAndDropEnabled) {
    return (
      <TailwindContents>
        <div ref={measureRef} className="flex grow flex-col">
          <PanelBankSectionsContainer
            isProjectReadOnly={!!panelBankProps.isProjectReadOnly}
          />
        </div>
      </TailwindContents>
    );
  }

  return (
    <div className="panel-bank__sections" ref={measureRef}>
      {panelBankWidth > 0 && (
        <PanelBankSections
          key={panelBankConfigRef.id}
          {...panelBankProps}
          panelBankConfigWithRefs={panelBankConfigWithRefs}
          panelBankSectionsDomRef={panelBankSectionsDomRef}
        />
      )}
    </div>
  );
};

export const PanelBankInner = memo(
  PanelBankInnerComp,
  (prevProps, nextProps) => {
    return (
      shallowEqual(
        prevProps.panelBankConfigWithRefs,
        nextProps.panelBankConfigWithRefs
      ) &&
      prevProps.setPanelSelection === nextProps.setPanelSelection &&
      shallowEqual(prevProps.panelBankProps, nextProps.panelBankProps)
    );
  }
);
